import { StyleSheet, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { mvs } from 'react-native-size-matters';

import { EpisodeControlsSectionTablet } from './EpisodeControlsSectionTablet';
import { useAppSelector } from '../../app/hooks';
import { COLORS } from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import useOrientation from '../../hooks/useOrientation';
import { PodcastEpisodeModel } from '../../models';
import CustomText from '../CustomText';

interface EpisodeHeaderTabletProps {
  episode: PodcastEpisodeModel;
}

export function EpisodeHeaderTablet({ episode }: EpisodeHeaderTabletProps) {
  const divRadius = useAppSelector(selectDivRadius);
  const defaultCover = useAppSelector(selectDefaultCover);

  // dynamic font sizes for tablet
  const episodeTitleFontSize = useAdjustedFontSize(mvs(24));
  const podcastTitleFontSize = useAdjustedFontSize(mvs(16));

  // portrait specific styles
  const orientation = useOrientation();
  const isPortrait = orientation === 'PORTRAIT';

  return (
    <View style={[styles.container, isPortrait && styles.portraitContainer]}>
      <View style={[styles.contentContainer]}>
        {/* episode image */}
        <View style={[styles.episodeImageContainer, { borderRadius: divRadius }]}>
          <FastImage
            source={{ uri: episode.image }}
            style={styles.episodeImage}
            resizeMode={FastImage.resizeMode.cover}
          />
        </View>

        {/* episode info */}
        <View style={styles.episodeInfo}>
          {/* episode title */}
          <CustomText
            style={[styles.episodeTitle, { fontSize: episodeTitleFontSize }]}
            weight="Bold"
            numberOfLines={2}
          >
            {episode.title}
          </CustomText>

          {/* podcast title */}
          <View style={styles.podcastDetailsContainer}>
            <FastImage
              source={episode.podcastLogo ? { uri: episode.podcastLogo } : defaultCover}
              style={[styles.podcastThumbnail, { borderRadius: mvs(divRadius) }]}
              resizeMode={FastImage.resizeMode.cover}
            />
            <CustomText
              style={[styles.podcastTitle, { fontSize: podcastTitleFontSize }]}
              weight="SemiBold"
              numberOfLines={1}
            >
              {episode.podcastTitle}
            </CustomText>
          </View>
          {!isPortrait && <EpisodeControlsSectionTablet {...episode} />}
        </View>
      </View>
      {isPortrait && <EpisodeControlsSectionTablet {...episode} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginBottom: mvs(32),
  },
  portraitContainer: {
    marginBottom: mvs(12),
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(32),
  },
  episodeImageContainer: {
    width: '33%',
    aspectRatio: '1/1',
    overflow: 'hidden',
  },
  episodeImage: {
    width: '100%',
    height: '100%',
  },
  episodeInfo: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
  },
  podcastDetailsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(12),
  },
  podcastThumbnail: {
    height: mvs(40),
    width: mvs(40),
  },
  podcastTitle: {
    color: COLORS.WHITE,
    opacity: 0.9,
    flex: 1,
  },
  episodeTitle: {
    color: COLORS.WHITE,
  },
});
